import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, Clock, Eye, Share2, ArrowLeft, MessageCircle, Languages, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, categoryName, timeAgoKannada } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdSlot from "@/components/AdSlot";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "ta", label: "Tamil", native: "தமிழ்" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
];

export default function Article() {
  const { id } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [related, setRelated] = useState([]);
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/articles/${id}`);
        setArticle(data);
        const [{ data: cList }, { data: relList }] = await Promise.all([
          api.get(`/articles/${id}/comments`),
          api.get(`/articles?category=${data.category}&limit=4`),
        ]);
        setComments(cList);
        setRelated(relList.filter((r) => r.id !== id).slice(0, 3));
        if (user && user !== false) {
          try {
            const { data: bs } = await api.get(`/bookmarks/status/${id}`);
            setBookmarked(bs.bookmarked);
          } catch {}
        }
      } catch {
        toast.error("ಲೇಖನ ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user]);

  const toggleBookmark = async () => {
    if (!user || user === false) {
      toast.error("ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ");
      return;
    }
    try {
      if (bookmarked) {
        await api.delete(`/bookmarks/${id}`);
        setBookmarked(false);
        toast.success("ಬುಕ್‌ಮಾರ್ಕ್ ತೆಗೆದುಹಾಕಲಾಗಿದೆ");
      } else {
        await api.post(`/bookmarks/${id}`);
        setBookmarked(true);
        toast.success("ಬುಕ್‌ಮಾರ್ಕ್ ಮಾಡಲಾಗಿದೆ");
      }
    } catch {
      toast.error("ದೋಷ ಸಂಭವಿಸಿದೆ");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    if (!user || user === false) {
      toast.error("ಕಾಮೆಂಟ್ ಮಾಡಲು ಲಾಗಿನ್ ಮಾಡಿ");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post(`/articles/${id}/comments`, { content: commentText.trim() });
      setComments([data, ...comments]);
      setCommentText("");
      toast.success("ಕಾಮೆಂಟ್ ಪ್ರಕಟಿಸಲಾಯಿತು");
    } catch (e) {
      toast.error("ಕಾಮೆಂಟ್ ಪ್ರಕಟಿಸಲಾಗಲಿಲ್ಲ");
    } finally {
      setPosting(false);
    }
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("ಲಿಂಕ್ ನಕಲಿಸಲಾಗಿದೆ");
    } catch {
      toast.error("ನಕಲಿಸಲಾಗಲಿಲ್ಲ");
    }
  };

  const shareToWhatsApp = () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${BACKEND}/api/share/article/${id}`;
    const text = `📰 ${article.title}\n\n${shareUrl}`;
    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, "_blank", "noreferrer");
  };

  const shareToWhatsAppWithPhoto = async () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${BACKEND}/api/share/article/${id}`;
    const summaryLine = article.summary ? `\n\n${article.summary}` : "";
    const text = `📰 *${article.title}*${summaryLine}\n\n${shareUrl}`;
    // 1) Download the article photo via backend proxy (bypasses CORS)
    try {
      toast.info("ಫೋಟೋ ಡೌನ್‌ಲೋಡ್ ಆಗುತ್ತಿದೆ...");
      const imgRes = await fetch(`${BACKEND}/api/share/article/${id}/image`);
      const blob = await imgRes.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
      a.download = `namaste-karnataka-${id.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch (e) {
      toast.error("ಫೋಟೋ ಡೌನ್‌ಲೋಡ್ ವಿಫಲ — ಟೆಕ್ಸ್ಟ್ ಮಾತ್ರ ಶೇರ್ ಆಗುತ್ತದೆ");
    }
    // 2) Copy the text to clipboard
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
    // 3) Open WhatsApp with pre-filled text
    toast.success("ಫೋಟೋ ಡೌನ್‌ಲೋಡ್ ✓ · ಟೆಕ್ಸ್ಟ್ ನಕಲಿಸಲಾಗಿದೆ ✓ — ಈಗ WhatsApp ನಲ್ಲಿ ಫೋಟೋ ಲಗತ್ತಿಸಿ");
    setTimeout(() => {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noreferrer");
    }, 800);
  };

  const translate = async (lang) => {
    if (translation?.lang === lang) return;
    setTranslating(true);
    try {
      const { data } = await api.post(`/articles/${id}/translate`, { target_lang: lang });
      setTranslation(data);
      toast.success(`Translated to ${LANGS.find((l) => l.code === lang)?.label}`);
    } catch (e) {
      toast.error("Translation failed. Try again.");
    } finally {
      setTranslating(false);
    }
  };

  const displayTitle = translation?.title || article?.title;
  const displaySummary = translation?.summary || article?.summary;
  const displayContent = translation?.content || article?.content;
  const isPremium = user && user !== false && user.subscription_tier === "premium";

  if (loading || !article) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-96" />
        <Skeleton className="h-4" />
        <Skeleton className="h-4" />
        <Skeleton className="h-4" />
      </div>
    );
  }

  return (
    <div className="grid gap-10 lg:grid-cols-12">
      <article className="lg:col-span-8" data-testid="article-detail">
        <Link
          to={`/category/${article.category}`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4 hover:underline"
          data-testid="back-to-category"
        >
          <ArrowLeft className="w-3 h-3" /> {categoryName(article.category)}
        </Link>

        <h1 className="font-kannada-heading text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="article-title">
          {displayTitle}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground font-kannada-body max-w-3xl">
          {displaySummary}
        </p>

        {translation && (
          <div className="mt-4 inline-flex items-center gap-2 border border-primary/40 bg-primary/5 px-3 py-1.5 rounded-sm text-xs" data-testid="translation-banner">
            <Languages className="w-3 h-3 text-primary" />
            <span className="font-semibold">
              Translated to {LANGS.find((l) => l.code === translation.lang)?.label}
            </span>
            <button
              onClick={() => setTranslation(null)}
              className="ml-2 hover:text-primary"
              data-testid="show-original-btn"
            >
              <X className="w-3 h-3" />
              <span className="sr-only">Show original</span>
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-4 pb-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9 rounded-none">
              <AvatarFallback className="rounded-none bg-foreground text-background text-xs font-bold">
                {article.author?.[0] || "ನ"}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-semibold">{article.author}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Clock className="w-3 h-3" /> {timeAgoKannada(article.created_at)} ·{" "}
                <Eye className="w-3 h-3" /> {article.views} views
              </div>
            </div>
          </div>
          <div className="ml-auto flex gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={translating} data-testid="translate-btn">
                  <Languages className="w-4 h-4 mr-1" />
                  {translating ? "Translating..." : "Translate"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => translate(l.code)}
                    data-testid={`translate-${l.code}`}
                    className="cursor-pointer"
                  >
                    <span className="font-semibold mr-2">{l.native}</span>
                    <span className="text-muted-foreground text-xs">{l.label}</span>
                  </DropdownMenuItem>
                ))}
                {translation && (
                  <DropdownMenuItem onClick={() => setTranslation(null)} className="cursor-pointer border-t border-border mt-1 pt-2">
                    <X className="w-3 h-3 mr-2" /> ಕನ್ನಡ (Original)
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleBookmark}
              data-testid="bookmark-btn"
            >
              {bookmarked ? (
                <BookmarkCheck className="w-4 h-4 mr-1 text-primary" />
              ) : (
                <Bookmark className="w-4 h-4 mr-1" />
              )}
              {bookmarked ? "ಉಳಿಸಲಾಗಿದೆ" : "ಉಳಿಸಿ"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="whatsapp-share-btn"
                  className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white hover:border-[#25D366]"
                >
                  <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={shareToWhatsAppWithPhoto} className="cursor-pointer" data-testid="whatsapp-share-with-photo">
                  <span className="mr-2">📷</span>
                  <div>
                    <div className="font-semibold text-sm">ಫೋಟೋ ಸಹಿತ ಶೇರ್</div>
                    <div className="text-[10px] text-muted-foreground">Share with photo attachment</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareToWhatsApp} className="cursor-pointer" data-testid="whatsapp-share-link-only">
                  <span className="mr-2">🔗</span>
                  <div>
                    <div className="font-semibold text-sm">ಲಿಂಕ್ ಮಾತ್ರ ಶೇರ್</div>
                    <div className="text-[10px] text-muted-foreground">Share link only (auto preview)</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={share} data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-1" /> ಹಂಚಿ
            </Button>
          </div>
        </div>

        <div className="mt-6 aspect-[16/9] overflow-hidden rounded-sm">
          <img src={article.image_url} alt="" className="w-full h-full object-cover" />
        </div>

        {!isPremium && <AdSlot slot="article_top" className="mt-6 h-24" />}

        <div className={`mt-8 text-lg leading-[1.9] whitespace-pre-line dropcap ${translation ? "" : "font-kannada-body"}`} data-testid="article-content">
          {displayContent}
        </div>

        {!isPremium && <AdSlot slot="article_bottom" className="mt-8 h-24" />}

        {/* Comments */}
        <section className="mt-14 border-t border-border pt-10" data-testid="comments-section">
          <h3 className="font-kannada-heading text-2xl flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" /> ಕಾಮೆಂಟ್‌ಗಳು ({comments.length})
          </h3>
          {user && user !== false ? (
            <form onSubmit={submitComment} className="mt-6 space-y-3">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="ನಿಮ್ಮ ಅಭಿಪ್ರಾಯ ಬರೆಯಿರಿ..."
                className="min-h-[100px] rounded-none"
                data-testid="comment-textarea"
              />
              <Button type="submit" disabled={posting || !commentText.trim()} data-testid="submit-comment-btn">
                {posting ? "ಪ್ರಕಟಿಸುತ್ತಿದೆ..." : "ಪ್ರಕಟಿಸಿ"}
              </Button>
            </form>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              ಕಾಮೆಂಟ್ ಮಾಡಲು{" "}
              <Link to="/login" className="text-primary font-semibold underline underline-offset-4">
                ಲಾಗಿನ್
              </Link>{" "}
              ಮಾಡಿ.
            </p>
          )}
          <div className="mt-8 space-y-5">
            {comments.length === 0 && (
              <p className="text-muted-foreground text-sm">ಇನ್ನೂ ಯಾವುದೇ ಕಾಮೆಂಟ್‌ಗಳಿಲ್ಲ. ಮೊದಲಿಗರಾಗಿ!</p>
            )}
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3 border-b border-border pb-4" data-testid={`comment-${c.id}`}>
                <Avatar className="w-9 h-9 rounded-none">
                  <AvatarFallback className="rounded-none bg-secondary text-secondary-foreground text-xs font-bold">
                    {c.user_name?.[0] || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">{c.user_name}</span>
                    <span className="text-xs text-muted-foreground">{timeAgoKannada(c.created_at)}</span>
                  </div>
                  <p className="text-sm font-kannada-body mt-1">{c.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </article>

      <aside className="lg:col-span-4 space-y-6" data-testid="related-sidebar">
        {!isPremium && <AdSlot slot="sidebar" className="h-64" />}
        <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground border-b border-border pb-2">
          ಸಂಬಂಧಿತ ಸುದ್ದಿಗಳು
        </div>
        {related.map((r, i) => (
          <div key={r.id}>
            <Link to={`/article/${r.id}`} className="group block" data-testid={`related-${r.id}`}>
              <div className="aspect-[16/10] overflow-hidden rounded-sm mb-2">
                <img src={r.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary">
                {categoryName(r.category)}
              </div>
              <h4 className="font-kannada-heading text-lg leading-snug mt-1 group-hover:underline">
                {r.title}
              </h4>
            </Link>
          </div>
        ))}
      </aside>
    </div>
  );
}
